// InstallableRail — generic catalog + instances rail/grid/list.
//
// Renders one of three layouts of installable items (integrations,
// libraries, MCP connectors, plugins, templates) for a given scopeType.
//
// Props (set on element or forwarded via parent):
//   domain        – 'integration'|'library'|'mcp'|'plugin'|'template'
//   scopeType     – 'org'|'workspace'|'project'
//                   NOTE: `scope` is a DOMQL v3.14 reserved keyword (CSS-variable
//                   scope mixin). Always use `scopeType` for this prop.
//   scopeId       – active org / workspace / project id
//   title         – rail header copy (optional)
//   emptyHint     – empty-state CTA copy (optional)
//   layout        – 'rail'|'grid'|'list'  (default: 'rail')
//   filter        – { category?, applicable_to?, … } extra catalogue filter
//   onAddClick    – optional hook before modal opens
//   onInstanceClick – optional manage-deeplink override
//
// Root state keys written by this component (all prefixed to avoid
// collisions when multiple rails are on the same page):
//   _installableRailLoading_<domain>_<scopeType>     boolean
//   _installableRailError_<domain>_<scopeType>       string|null
//   _installableRailKinds_<domain>_<scopeType>       array of catalogue rows
//   _installableRailInstances_<domain>_<scopeType>   array of instance rows
//
// Data contract (Phase 1 — display.* columns assumed present):
//   kind row: { kind, domain, category, display: { icon, label, tagline, color? },
//               applicable_to_scopes, enabled, … }
//   instance:  { kind, scope_type, scope_id, slug, display_name, enabled, … }

// ── State key helpers ─────────────────────────────────────────────────

const _railKey = (domain, scopeType) => `_installableRail_${domain}_${scopeType}`

const _getKinds = (root, domain, scopeType) =>
  root?.[`${_railKey(domain, scopeType)}_kinds`] || []

const _getInstances = (root, domain, scopeType) =>
  root?.[`${_railKey(domain, scopeType)}_instances`] || []

const _isLoading = (root, domain, scopeType) =>
  !!root?.[`${_railKey(domain, scopeType)}_loading`]

const _getError = (root, domain, scopeType) =>
  root?.[`${_railKey(domain, scopeType)}_error`] || null

const _hasInstalled = (instances, kind) =>
  instances.some((i) => i.kind === kind)

// ── Data loading helpers ───────────────────────────────────────────────
// Keeps network calls out of the component declarations so they're
// plain async functions (allowed — they don't use el.*).

const _POLL_INTERVAL = 30_000

// Governance Supabase credentials — catalog (integration_kinds) is
// publicly readable via the anon key (migration 0063 + 0102 RLS policy).
// These are the published fallback values from sdk-bridge/src/env.js;
// they are NOT secrets. The same values are embedded in the marketplace
// build today (see workspace/packages/marketplace/dist).
const _GOV_SUPABASE_URL = 'https://becrzpqaiovbvfmrosro.supabase.co'
const _GOV_ANON_KEY = 'sb_publishable_suMZ-1Crw-304sk_MY-VZA_8aU6ZFfa'

// Channel-resolved API base — kept for non-catalog endpoints
// (org-integrations instances fetch still goes through the Symbols server).
import { apiUrl as _channelApiUrl } from '@symbo.ls/channels'

const _apiBase = () => {
  try {
    return _channelApiUrl().replace(/\/+$/, '')
  } catch {
    return _channelApiUrl()
  }
}

const _getToken = () => {
  if (typeof window === 'undefined') return null
  return (
    window.localStorage?.getItem('symbols_bridge_access_token') ||
    window.localStorage?.getItem('symbols_access_token') ||
    null
  )
}

// Bug A+B fix: query Supabase REST directly for integration_kinds.
//
// Previously this called /core/integration-kinds on the Symbols server,
// which doesn't exist (404). The catalog lives in the governance Supabase
// `integration_kinds` table (migration 0063). PostgREST's `cs.{}` (contains)
// operator is native there — no custom parser needed.
// Matches the pattern used by marketplace's hydrateLiveCatalog (#3741).
const _fetchKinds = async (domain, scopeType) => {
  if (typeof window === 'undefined') return []
  const params = new URLSearchParams({ enabled: 'eq.true' })
  if (scopeType) params.set('applicable_to_scopes', `cs.{${scopeType}}`)
  try {
    const res = await fetch(
      `${_GOV_SUPABASE_URL}/rest/v1/integration_kinds?${params}`,
      {
        headers: {
          apikey: _GOV_ANON_KEY,
          Authorization: `Bearer ${_GOV_ANON_KEY}`
        }
      }
    )
    if (!res.ok) return []
    const json = await res.json().catch(() => null)
    // PostgREST returns a plain array; filter enabled defensively.
    return (Array.isArray(json) ? json : json?.data || []).filter((k) => k.enabled !== false)
  } catch {
    return []
  }
}

// Fetch installed instances for a given scopeType.
// Bug C fix: scopeId is guaranteed to be a resolved value by the time it
// reaches here — see _resolveElProp usage in onInit below. Guard added here
// as a belt-and-suspenders check: skip the fetch rather than send a null/
// function-literal string as a query param.
const _fetchInstances = async (domain, scopeType, scopeId) => {
  if (typeof window === 'undefined') return []
  // Skip fetch when scopeId hasn't resolved yet (e.g. orgId not loaded).
  if (!scopeId || typeof scopeId === 'function') return []
  if (!scopeId && scopeType !== 'org') return []
  const token = _getToken()
  if (!token) return []
  const headers = { Authorization: `Bearer ${token}` }
  const params = new URLSearchParams({ scope_type: scopeType })
  if (scopeId) params.set('scope_id', scopeId)
  // The endpoint varies by domain; fall back to org-integrations for integrations.
  const segment =
    domain === 'integration'
      ? 'org-integrations'
      : `${domain}-instances`
  try {
    const res = await fetch(
      `${_apiBase()}/core/${segment}?${params}`,
      { headers }
    )
    if (!res.ok) return []
    const json = await res.json().catch(() => null)
    return json?.data || json?.integrations || json?.instances || json || []
  } catch {
    return []
  }
}

// Bug C fix: DOMQL v3.14 props may be function-typed when accessed via `el.X`
// in onInit — the framework doesn't auto-evaluate factory props on access.
// Resolve here before passing to async fetch helpers so no function literal
// ever reaches a URLSearchParams value.
const _resolveElProp = (el, s, prop) => {
  const v = el[prop]
  return typeof v === 'function' ? v(el, s) : v
}

// Called onInit and by the realtime/poll tick. Writes into root state.
const _loadRail = async (root, domain, scopeType, scopeId) => {
  if (!domain || !scopeType) return
  const loadingKey = `${_railKey(domain, scopeType)}_loading`
  const errorKey = `${_railKey(domain, scopeType)}_error`
  const kindsKey = `${_railKey(domain, scopeType)}_kinds`
  const instancesKey = `${_railKey(domain, scopeType)}_instances`

  // Only start a fresh load if not already in flight.
  if (root?.[loadingKey]) return
  root.update({ [loadingKey]: true, [errorKey]: null })

  try {
    const [kinds, instances] = await Promise.all([
      _fetchKinds(domain, scopeType),
      _fetchInstances(domain, scopeType, scopeId)
    ])
    root.update({
      [kindsKey]: kinds,
      [instancesKey]: instances,
      [loadingKey]: false
    })
  } catch (err) {
    root.update({
      [loadingKey]: false,
      [errorKey]: err?.message || 'Load failed'
    })
  }
}

// ── Add button ────────────────────────────────────────────────────────

export const InstallableRailAddButton = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  gap: 'Y',
  align: 'center center',
  padding: 'Y Z',
  round: 'Z2',
  borderWidth: '1px',
  borderStyle: 'dashed',
  borderColor: 'line',
  background: 'transparent',
  color: 'blue',
  fontSize: 'Z2',
  fontWeight: '600',
  cursor: 'pointer',
  flexShrink: '0',
  transition: 'B defaultBezier',
  transitionProperty: 'background, border-color',
  ':hover': {
    background: 'blue.1',
    borderColor: 'blue.5'
  },
  '@media(prefers-reduced-motion:reduce)': {
    transition: 'none'
  },

  Icon: {
    name: 'plus',
    fontSize: 'Z2',
    color: 'blue'
  },

  Label: {
    tag: 'span',
    text: (el, s) => `Add ${s.domain || 'item'}`
  },

  onClick: (e, el, s) => {
    if (typeof s.onAddClick === 'function') {
      s.onAddClick(s.domain)
    }
    el.call('openInstallableModal', {
      domain: s.domain,
      defaultScope: { type: s.scopeType, id: s.scopeId },
      catalogFilter: s.filter
    })
  }
}

// ── Empty state ───────────────────────────────────────────────────────

export const InstallableRailEmpty = {
  flow: 'y',
  gap: 'Z',
  align: 'center center',
  padding: 'B A',
  round: 'A',
  borderWidth: '1px',
  borderStyle: 'dashed',
  borderColor: 'line',
  background: 'overlayStrong',
  width: '100%',

  EmptyIcon: {
    extends: 'Icon',
    name: (el, s) => {
      const icons = {
        integration: 'grid',
        library: 'bookmark',
        mcp: 'terminal',
        plugin: 'puzzle',
        template: 'layers'
      }
      return icons[s.domain] || 'grid'
    },
    fontSize: 'D',
    color: 'caption'
  },

  EmptyLabel: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z2',
    fontWeight: '600',
    color: 'title',
    textAlign: 'center',
    text: (el, s) =>
      s.emptyHint ||
      `Add your first ${s.domain || 'item'}`
  },

  EmptyHint: {
    tag: 'p',
    margin: '0',
    fontSize: 'Y2',
    fontWeight: '400',
    color: 'caption',
    textAlign: 'center',
    text: (el, s) => {
      const domain = s.domain || 'item'
      const scopeType = s.scopeType || 'workspace'
      return `No ${domain}s installed for this ${scopeType} yet.`
    }
  },

  AddButton: {
    extends: 'InstallableRailAddButton',
    domain: (el, s) => s.domain,
    scopeType: (el, s) => s.scopeType,
    scopeId: (el, s) => s.scopeId,
    filter: (el, s) => s.filter,
    onAddClick: (el, s) => s.onAddClick
  }
}

// ── Loading skeleton ──────────────────────────────────────────────────

export const InstallableRailSkeleton = {
  flow: 'x',
  gap: 'Z',
  align: 'center left',

  childExtends: {
    round: 'Y',
    background: 'line',
    height: 'B',
    width: 'E',
    flexShrink: '0',
    // Pulse animation via opacity — respects prefers-reduced-motion at
    // the keyframe level (no JS toggle needed).
    animation: 'pulse 1.5s ease-in-out infinite',
    '@media(prefers-reduced-motion:reduce)': {
      animation: 'none',
      opacity: '0.5'
    }
  },

  Item_1: { width: 'E' },
  Item_2: { width: 'D2', opacity: '0.8' },
  Item_3: { width: 'D2', opacity: '0.6' }
}

// ── Rail header ───────────────────────────────────────────────────────

export const InstallableRailHeader = {
  flow: 'x',
  gap: 'Z',
  align: 'center space-between',

  TitleGroup: {
    flow: 'x',
    gap: 'Y',
    align: 'center left',

    TitleIcon: {
      extends: 'Icon',
      name: (el, s) => {
        const icons = {
          integration: 'grid',
          library: 'bookmark',
          mcp: 'terminal',
          plugin: 'puzzle',
          template: 'layers'
        }
        return icons[s.domain] || 'grid'
      },
      fontSize: 'B',
      color: 'caption',
      hide: (el, s) => !s.domain
    },

    Title: {
      tag: 'h3',
      margin: '0',
      fontSize: 'Z2',
      fontWeight: '700',
      color: 'title',
      hide: (el, s) => !s.title,
      text: (el, s) => s.title || ''
    }
  },

  AddButton: {
    extends: 'InstallableRailAddButton',
    domain: (el, s) => s.domain,
    scopeType: (el, s) => s.scopeType,
    scopeId: (el, s) => s.scopeId,
    filter: (el, s) => s.filter,
    onAddClick: (el, s) => s.onAddClick,
    // In list layout hide from header — empty state handles it.
    hide: (el, s) => s.layout === 'list'
  }
}

// ── Main export ───────────────────────────────────────────────────────

export const InstallableRail = {
  flow: 'y',
  gap: 'Z',

  // ── Initialise: load catalogue + instances, start poll ──────────────
  onInit: (el, s) => {
    const root = s.root
    // Bug C fix: resolve DOMQL v3.14 props via _resolveElProp — accessing
    // el.X for a factory prop returns the function, not its result.
    const domain = _resolveElProp(el, s, 'domain')
    const scopeType = _resolveElProp(el, s, 'scopeType')
    const scopeId = _resolveElProp(el, s, 'scopeId')
    if (!domain || !scopeType) return

    // Kick off the first fetch.
    _loadRail(root, domain, scopeType, scopeId)

    // Poll every 30 s as fallback realtime.
    // Store the timer id on the element (transient; not root state).
    el._railPollTimer = setInterval(() => {
      // Re-resolve scopeId on each tick — orgId may become available
      // after the initial render (e.g. lazy auth hydration).
      const latestScopeId = _resolveElProp(el, s, 'scopeId')
      _loadRail(root, domain, scopeType, latestScopeId)
    }, _POLL_INTERVAL)
  },

  // Clean up poll timer on destroy.
  onDestroy: (el, s) => {
    if (el._railPollTimer) {
      clearInterval(el._railPollTimer)
      el._railPollTimer = null
    }
  },

  // ── Header ───────────────────────────────────────────────────────────
  InstallableRailHeader: {
    domain: (el, s) => el.parent?.domain,
    title: (el, s) => el.parent?.title,
    scopeType: (el, s) => el.parent?.scopeType,
    scopeId: (el, s) => el.parent?.scopeId,
    filter: (el, s) => el.parent?.filter,
    layout: (el, s) => el.parent?.layout || 'rail',
    onAddClick: (el, s) => el.parent?.onAddClick,
    hide: (el, s) =>
      !el.parent?.title && !el.parent?.domain
  },

  // ── Loading skeleton (while first fetch is in flight) ────────────────
  InstallableRailSkeleton: {
    hide: (el, s) => {
      const p = el.parent
      if (!p) return true
      return !_isLoading(s.root, p.domain, p.scopeType)
    }
  },

  // ── Error banner ─────────────────────────────────────────────────────
  ErrorBanner: {
    flow: 'x',
    gap: 'Y',
    align: 'center left',
    padding: 'Y Z',
    round: 'Y',
    background: 'red.1',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'red.5',
    hide: (el, s) => {
      const p = el.parent
      if (!p) return true
      return !_getError(s.root, p.domain, p.scopeType)
    },

    Icon: {
      name: 'alert circle',
      fontSize: 'Z2',
      color: 'red'
    },

    Message: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'red',
      text: (el, s) => {
        const p = el.parent
        if (!p) return ''
        return _getError(s.root, p.domain, p.scopeType) || ''
      }
    },

    RetryButton: {
      tag: 'button',
      type: 'button',
      padding: 'X2 Y',
      round: 'Y',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      fontSize: 'Y1',
      color: 'red',
      textDecoration: 'underline',
      text: 'Retry',
      onClick: (e, el, s) => {
        const p = el.parent?.parent
        if (!p) return
        _loadRail(s.root, p.domain, p.scopeType, p.scopeId)
      }
    }
  },

  // ── Empty state ───────────────────────────────────────────────────────
  InstallableRailEmpty: {
    domain: (el, s) => el.parent?.domain,
    scopeType: (el, s) => el.parent?.scopeType,
    scopeId: (el, s) => el.parent?.scopeId,
    filter: (el, s) => el.parent?.filter,
    emptyHint: (el, s) => el.parent?.emptyHint,
    onAddClick: (el, s) => el.parent?.onAddClick,
    hide: (el, s) => {
      const p = el.parent
      if (!p) return true
      // Show empty only when not loading, no error, and no kinds found.
      if (_isLoading(s.root, p.domain, p.scopeType)) return true
      if (_getError(s.root, p.domain, p.scopeType)) return true
      const kinds = _getKinds(s.root, p.domain, p.scopeType)
      return kinds.length > 0
    }
  },

  // ── Rail layout (horizontal scroll strip) ────────────────────────────
  RailStrip: {
    flow: 'x',
    gap: 'Z',
    overflowX: 'auto',
    overflowY: 'hidden',
    padding: 'X2 0',
    // Hide scrollbar visually but keep scroll accessible.
    scrollbarWidth: 'none',
    '::-webkit-scrollbar': { display: 'none' },

    hide: (el, s) => {
      const p = el.parent
      if (!p || (p.layout || 'rail') !== 'rail') return true
      if (_isLoading(s.root, p.domain, p.scopeType)) return true
      return _getKinds(s.root, p.domain, p.scopeType).length === 0
    },

    childExtends: 'InstallableRailItem',
    childrenAs: 'state',
    children: (el, s) => {
      const p = el.parent
      if (!p) return []
      const domain = p.domain
      const scopeType = p.scopeType
      const kinds = _getKinds(s.root, domain, scopeType)
      const instances = _getInstances(s.root, domain, scopeType)
      return kinds.map((k) => ({
        domain,
        kind: k.kind,
        display: k.display || { icon: k.icon, label: k.label, tagline: k.description },
        installed: _hasInstalled(instances, k.kind),
        _layout: 'rail'
      }))
    },

    // Trailing add button at the end of the chip row.
    AddButton: {
      extends: 'InstallableRailAddButton',
      domain: (el, s) => el.parent?.parent?.domain,
      scopeType: (el, s) => el.parent?.parent?.scopeType,
      scopeId: (el, s) => el.parent?.parent?.scopeId,
      filter: (el, s) => el.parent?.parent?.filter,
      onAddClick: (el, s) => el.parent?.parent?.onAddClick
    }
  },

  // ── Grid layout (responsive card grid) ───────────────────────────────
  GridStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 'Z',

    hide: (el, s) => {
      const p = el.parent
      if (!p || (p.layout || 'rail') !== 'grid') return true
      if (_isLoading(s.root, p.domain, p.scopeType)) return true
      return _getKinds(s.root, p.domain, p.scopeType).length === 0
    },

    childExtends: 'InstallableRailItem',
    childrenAs: 'state',
    children: (el, s) => {
      const p = el.parent
      if (!p) return []
      const domain = p.domain
      const scopeType = p.scopeType
      const kinds = _getKinds(s.root, domain, scopeType)
      const instances = _getInstances(s.root, domain, scopeType)
      return kinds.map((k) => ({
        domain,
        kind: k.kind,
        display: k.display || { icon: k.icon, label: k.label, tagline: k.description },
        installed: _hasInstalled(instances, k.kind),
        _layout: 'grid'
      }))
    }
  },

  // ── List layout (vertical management list) ────────────────────────────
  ListStrip: {
    flow: 'y',
    round: 'A',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'line',
    background: 'document',
    overflow: 'hidden',

    hide: (el, s) => {
      const p = el.parent
      if (!p || (p.layout || 'rail') !== 'list') return true
      if (_isLoading(s.root, p.domain, p.scopeType)) return true
      return _getKinds(s.root, p.domain, p.scopeType).length === 0
    },

    childExtends: 'InstallableRailItem',
    childrenAs: 'state',
    children: (el, s) => {
      const p = el.parent
      if (!p) return []
      const domain = p.domain
      const scopeType = p.scopeType
      const kinds = _getKinds(s.root, domain, scopeType)
      const instances = _getInstances(s.root, domain, scopeType)
      return kinds.map((k) => ({
        domain,
        kind: k.kind,
        display: k.display || { icon: k.icon, label: k.label, tagline: k.description },
        installed: _hasInstalled(instances, k.kind),
        _layout: 'list'
      }))
    },

    // List footer: "Add" button at the bottom of the list.
    ListAddRow: {
      flow: 'x',
      gap: 'Y',
      align: 'center left',
      padding: 'Y Z2',
      borderTop: '1px',
      borderTopStyle: 'solid',
      borderTopColor: 'line',

      AddButton: {
        extends: 'InstallableRailAddButton',
        domain: (el, s) => el.parent?.parent?.parent?.domain,
        scopeType: (el, s) => el.parent?.parent?.parent?.scopeType,
        scopeId: (el, s) => el.parent?.parent?.parent?.scopeId,
        filter: (el, s) => el.parent?.parent?.parent?.filter,
        onAddClick: (el, s) => el.parent?.parent?.parent?.onAddClick
      }
    }
  }
}
